import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DevicesIcon from '@mui/icons-material/Devices';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PrintIcon from '@mui/icons-material/Print';
import { Box, ButtonBase, useMediaQuery, useTheme } from '@mui/material';

import DownloadIcon from '~/assets/icons/download.svg?react';
import PauseIcon from '~/assets/icons/pause.svg?react';
import EditIcon from '~/assets/icons/pen.svg?react';
import ExternalLink from '~/components/atoms/external-link/ExternalLink';
import ScansCount from '~/components/atoms/ScansCount';
import BasicTable from '~/components/organisms/BasicTable';
import GraphTemplate from '~/components/organisms/GraphTemplate';
import Notification from '~/components/organisms/Notification/Notification';
import { AlertContext } from '~/components/organisms/Notification/utils/AlertContextProvider';
import Accordion from '~/components/ui/Accordion/Accordion';
import { ConfirmModal } from '~/components/ui/ConfirmModal/ConfirmModal';
import DownloadContent from '~/components/ui/DownloadContent';
import { Text } from '~/components/ui/text';
import { TABLET_MIN_WIDTH } from '~/constants/common';
import { REDIRECT_QR_URI } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import { useQrCodeById } from '~/queries/qrCode';
import { useActivateQrs, useDeactivateQrs } from '~/queries/qrCodeTable';
import { getEditQrLink } from '~/utils/qr-code';

import * as S from './styles';

export default function MyQRDetail() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(TABLET_MIN_WIDTH));

  const navigate = useNavigate();
  const location = useLocation();
  const { qrId } = location.state;
  const { data: detailsData, refetch, isFetching } = useQrCodeById({ qrCodeId: qrId, select: (res) => res.data });
  const { mutate: activateMyQrItems } = useActivateQrs();
  const { mutate: deactivateMyQrItems } = useDeactivateQrs();
  const { actions } = useContext(AlertContext);

  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [openImage, setOpenImage] = useState(false);

  const notification = () => {
    actions.removeAlert({ id: 1 });
    actions.addAlert({
      text: t('labels:pauseQrCodeNotification'),
      type: 'error',
      variant: 'filled',
      id: 1,
    });
  };

  const handleChangeQrStatus = () => {
    if (detailsData.isActive) {
      deactivateMyQrItems([detailsData.id], {
        onSuccess: () => {
          refetch();
          notification();
        },
      });
    } else {
      activateMyQrItems([detailsData.id], {
        onSuccess: () => refetch(),
      });
    }
  };

  useEffect(() => {
    notification();
  }, []);

  if (!detailsData) {
    return;
  }

  const { scansOs, scansCountry, scansCity, scansBrowser, scansLanguage, totalScans } = detailsData.statistic || {};

  const mapCurrentStatistic = (array = [], count: string, type: string) => {
    return array
      ?.sort((a, b) => b[count] - a[count])
      ?.map((item, index) => {
        return {
          id: index + 1,
          type: item[type] || 'Not Found',
          scansCount: item[count],
          rate: (item[count] / totalScans) * 100,
        };
      });
  };

  const accordionsData = [
    {
      data: mapCurrentStatistic(scansOs, 'osCount', 'operatingSystem'),
      summary: 'qrDetail:titleScanOC',
      title: 'qrDetail:oc',
    },
    {
      data: mapCurrentStatistic(scansCountry, 'scansCountryCount', 'country'),
      summary: 'qrDetail:titleScanCountry',
      title: 'qrDetail:country',
    },
    {
      data: mapCurrentStatistic(scansCity, 'scansCityCount', 'city'),
      summary: 'qrDetail:titleScanRegion',
      title: 'qrDetail:region',
    },
    {
      data: mapCurrentStatistic(scansBrowser, 'scansBrowserCount', 'browser'),
      summary: 'qrDetail:titleScanBrowser',
      title: 'qrDetail:browser',
    },
    {
      data: mapCurrentStatistic(scansLanguage, 'scansLanguageCount', 'language'),
      summary: 'qrDetail:titleScanLang',
      title: 'qrDetail:Language',
    },
  ];

  const getLink = () => {
    let baseLink = `${REDIRECT_QR_URI}${PATH.S(detailsData.uri)}`;
    if (detailsData.isPasswordProtected) {
      baseLink += '?password_required=true';
    }
    return baseLink;
  };

  return (
    <Box sx={S.detailsContainer}>
      {!detailsData.isActive && <Notification customColor="var(--purple)" isCloseIcon={true} />}
      <Box sx={S.buttonsGroup}>
        <ButtonBase sx={S.actionBtn} className="back-btn" onClick={() => navigate(-1)}>
          <ArrowBackRoundedIcon fontSize="small" />
          {!isMobile && t('buttons:back')}
        </ButtonBase>
        <ButtonBase sx={S.actionBtn} className={isFetching ? 'loading' : ''} onClick={() => handleChangeQrStatus()}>
          {detailsData.isActive ? (
            <>
              <PauseIcon /> {!isMobile && t('buttons:pause')}
            </>
          ) : (
            <>
              <PlayArrowRoundedIcon fontSize="small" /> {!isMobile && t('buttons:start')}
            </>
          )}
        </ButtonBase>
        <ButtonBase sx={S.actionBtn} onClick={() => setOpenDownloadModal(true)}>
          <DownloadIcon />
          {!isMobile && t('buttons:download')}
        </ButtonBase>
        <ButtonBase sx={S.actionBtn} onClick={() => navigate(getEditQrLink(detailsData))}>
          <EditIcon /> {!isMobile && t('buttons:edit')}
        </ButtonBase>
      </Box>
      <Text variant="h1.7">{detailsData.name || 'Untitled'}</Text>
      <Box sx={S.subLabelsContainer}>
        <Text>{detailsData.micrositeTypeLabel}</Text>
        <ExternalLink
          text={
            detailsData.micrositeType === 'website' ? detailsData?.microsite?.url : `qbn.sh${PATH.S(detailsData.uri)}`
          }
          isIcon={true}
          redirectUrl={getLink()}
          sx={{ maxWidth: '200px' }}
        />
        <Box sx={S.subLabelsDate}>
          <CalendarTodayIcon fontSize="small" htmlColor="var(--grey400)" />
          <Text variant="inputSmall" sx={{ color: 'var(--grey400)' }}>
            {detailsData?.createdAt}
          </Text>
        </Box>
      </Box>

      {/* Total Scans */}
      <Box sx={S.mainContainer}>
        <Box sx={S.qrContainer}>
          <ScansCount sx={S.scansCount} count={detailsData?.statistic?.totalScans} />
          <Box gap="8px">
            <Box sx={S.qrDevice}>
              <DevicesIcon htmlColor="var(--grey400)" fontSize="small" />
              <Text variant="h3">0 - </Text>
              <Text variant="input15">{t('qrDetail:media')}</Text>
            </Box>
            <Box sx={S.qrDevice}>
              <PrintIcon htmlColor="var(--grey400)" fontSize="small" />
              <Text variant="h3">0 - </Text>
              <Text variant="input15">{t('qrDetail:print')}</Text>
            </Box>
          </Box>
          <ButtonBase sx={S.qrImage} onClick={() => setOpenImage(true)}>
            <img src={detailsData?.qrImage} alt="qr image" />
          </ButtonBase>
        </Box>
      </Box>

      {/*Graph */}
      <Box sx={S.mainContainer}>
        <Text variant="h3">{t('qrDetail:titleScanAtivity')}</Text>
        <GraphTemplate qrId={qrId} />
      </Box>

      {/* Accordions */}
      <Box paddingBottom="20px">
        {accordionsData.map((item, index) => {
          return (
            <Accordion
              key={index}
              sx={S.accordion}
              summary={t(item.summary)}
              details={<BasicTable className="basic-table" data={item.data} title={t(item.title)} />}
            />
          );
        })}
      </Box>

      {/* Modals */}
      <ConfirmModal onClose={() => setOpenDownloadModal(false)} open={openDownloadModal}>
        <DownloadContent
          downloadIdsWithTypes={{
            id: detailsData.id,
            type: detailsData.micrositeType ? detailsData.micrositeType : detailsData.content?.type,
          }}
          imageName={detailsData.name}
        />
      </ConfirmModal>
      <ConfirmModal onClose={() => setOpenImage(false)} open={openImage} img={detailsData?.qrImage} />
    </Box>
  );
}
